import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { resetPassword } from 'aws-amplify/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, MailCheck } from 'lucide-react';
import { analytics } from '@/lib/analytics';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

interface ForgotPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBackToLogin?: () => void;
  onCodeSent?: (email: string) => void;
}

export function ForgotPasswordModal({
  open,
  onOpenChange,
  onBackToLogin,
  onCodeSent,
}: ForgotPasswordModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setIsLoading(true);

    try {
      analytics.event('forgot_password_submit', {
        email: data.email,
      });

      // Call Amplify resetPassword
      const output = await resetPassword({ username: data.email });

      // Handle next steps from Cognito
      const { nextStep } = output;

      if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        analytics.conversion('password_reset_sent');
        setSubmittedEmail(data.email);
        setIsSuccess(true);
      }
    } catch (error: any) {
      console.error('Password reset error:', error);

      // Handle specific Cognito errors
      if (error.name === 'UserNotFoundException') {
        // Security: Don't reveal if user exists - still show success
        setSubmittedEmail(data.email);
        setIsSuccess(true);
        analytics.conversion('password_reset_sent');
      } else if (error.name === 'LimitExceededException') {
        form.setError('email', {
          type: 'manual',
          message: 'Too many requests. Please try again later.',
        });
      } else if (error.name === 'InvalidParameterException') {
        form.setError('email', {
          type: 'manual',
          message: 'Invalid email format.',
        });
      } else {
        form.setError('email', {
          type: 'manual',
          message: 'Failed to send reset email. Please try again.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setIsSuccess(false);
    setSubmittedEmail('');
    form.reset();
    onOpenChange(false);
    onBackToLogin?.();
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      // Reset state when closing
      setTimeout(() => {
        setIsSuccess(false);
        setSubmittedEmail('');
        form.reset();
      }, 200);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {isSuccess ? 'Check your email' : 'Reset password'}
          </DialogTitle>
          <DialogDescription>
            {isSuccess
              ? `We've sent password reset instructions to ${submittedEmail}`
              : "Enter your email address and we'll send you instructions to reset your password"}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="space-y-6">
            <div className="flex flex-col items-center justify-center py-4">
              <MailCheck className="w-16 h-16 text-blue-600 mb-4" />
              <p className="text-sm text-center text-muted-foreground mb-2">
                If an account exists for {submittedEmail}, you will receive a 6-digit code to reset
                your password.
              </p>
              <p className="text-xs text-center text-muted-foreground">
                Check your email inbox (and spam folder).
              </p>
            </div>

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => {
                  onCodeSent?.(submittedEmail);
                  analytics.event('password_reset_code_entry', { email: submittedEmail });
                }}
              >
                I have a code
              </Button>

              <Button variant="outline" className="w-full" onClick={handleBackToLogin}>
                Back to login
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Codes expire after 24 hours.
              </p>
            </div>
          </div>
        ) : (
          <>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          autoComplete="email"
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending instructions...
                    </>
                  ) : (
                    'Send reset instructions'
                  )}
                </Button>
              </form>
            </Form>

            <div className="text-center mt-6">
              <Button
                variant="link"
                className="px-0 text-sm h-auto"
                type="button"
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                ← Back to login
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
